<template>
  <!-- Incident of a class from the teacher's app (Miguel, points 3.4 and
       3.6): shows the incident if the class has one, or «Registrar
       incidencia» when the school and the teacher's permission allow it and
       the class has started. The form has the three ways out: recover the
       minutes left, cancel, or count it as given. -->
  <div class="incident-sheet" v-if="info">
    <div class="incident-done" v-if="info.incident">
      <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
      <div>
        <b>{{ s.registered }} · {{ s.types[info.incident.type] }}</b>
        <span>{{ info.incident.minutes_given }} {{ s.given }} · {{ s.resolutions[info.incident.resolution] }}</span>
        <span v-if="info.incident.resolution === 'recover'">{{ s.recovery }}</span>
        <span v-if="info.incident.resolution === 'cancel'">{{ info.incident.refunded ? s.refunded : s.not_refunded }}</span>
        <i>«{{ info.incident.description }}»</i>
      </div>
    </div>
    <button class="incident-open" v-else-if="info.can_register" v-on:click="open()">
      <font-awesome-icon :icon="['fas', 'exclamation-triangle']" /> {{ s.button }}
    </button>

    <transition name="fade">
      <div class="incident-backdrop" v-if="form.open" v-on:click.self="form.open = false">
        <div class="incident-form">
          <h3>{{ s.title }}</h3>

          <label>{{ s.what }}</label>
          <div class="incident-chips">
            <button v-for="t in info.types" :key="t" :class="{selected: form.type === t}" v-on:click="form.type = t">{{ s.types[t] }}</button>
          </div>

          <label>{{ s.description }}</label>
          <textarea maxlength="2000" :placeholder="s.description_placeholder" v-model="form.description"></textarea>

          <label>{{ s.minutes }} ({{ s.of }} {{ info.duration }})</label>
          <input type="number" min="0" :max="info.duration" v-model.number="form.minutes" />

          <label>{{ s.resolution }}</label>
          <div class="incident-resolutions">
            <button v-for="r in ['recover', 'cancel', 'close']" :key="r" :class="{selected: form.resolution === r}" v-on:click="form.resolution = r">
              <b>{{ s.resolutions[r] }}</b>
              <span>{{ s.explain[r] }}</span>
            </button>
          </div>

          <div class="incident-row" v-if="form.resolution === 'recover'">
            <div>
              <label>{{ s.recovery_date }}</label>
              <input type="date" v-model="form.date" />
            </div>
            <div>
              <label>{{ s.recovery_start }}</label>
              <input type="time" step="900" v-model="form.start" />
            </div>
            <div>
              <label>{{ s.recovery_minutes }}</label>
              <input type="number" min="1" :max="info.duration" v-model.number="form.recoveryMinutes" />
            </div>
          </div>
          <label class="incident-check" v-if="form.resolution === 'cancel'">
            <input type="checkbox" v-model="form.refund" /> {{ s.refund }}
          </label>

          <div class="incident-actions">
            <button class="incident-secondary" :disabled="saving" v-on:click="form.open = false">{{ s.back }}</button>
            <button class="h-button default" :disabled="saving" v-on:click="save()">{{ s.save }}</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'incident-sheet-component',
  props: {
    app: Object,
    practiceId: [Number, String]
  },
  data () {
    return {
      info: null,
      saving: false,
      form: {
        open: false,
        type: 'breakdown',
        description: '',
        minutes: 0,
        resolution: 'recover',
        date: '',
        start: '',
        recoveryMinutes: 0,
        refund: true
      }
    }
  },
  computed: {
    s () {
      return this.app.strings.components.practice.incident
    }
  },
  watch: {
    practiceId () {
      this.load()
    },
    'form.minutes' (value) {
      // The recovery is, by default, the minutes left.
      if (this.info) {
        this.form.recoveryMinutes = Math.max(1, this.info.duration - (parseInt(value) || 0))
      }
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    headers () {
      return {Authorization: 'Token ' + this.app.token}
    },
    load () {
      if (!this.practiceId || this.app.user.role !== 'teacher') {
        return
      }
      this.$http.get(this.app.url.api + this.app.endpoints.practice.incident.read + this.practiceId, {headers: this.headers()}).then(response => {
        if (response.status === 200 && response.body.success === true) {
          this.info = response.body.data
        }
      }, () => {})
    },
    open () {
      let tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      let pad = (n) => (n < 10 ? '0' : '') + n
      this.form = Object.assign(this.form, {
        open: true,
        type: 'breakdown',
        description: '',
        minutes: Math.floor(this.info.duration / 2),
        resolution: 'recover',
        date: tomorrow.getFullYear() + '-' + pad(tomorrow.getMonth() + 1) + '-' + pad(tomorrow.getDate()),
        start: '',
        refund: true
      })
      this.form.recoveryMinutes = this.info.duration - this.form.minutes
    },
    save () {
      if (this.form.description.trim() === '') {
        this.$swal({title: this.s.required, icon: 'warning'})
        return
      }
      if (this.form.resolution === 'recover' && (!this.form.date || !this.form.start)) {
        this.$swal({title: this.s.required_recovery, icon: 'warning'})
        return
      }
      let incident = {
        type: this.form.type,
        description: this.form.description.trim(),
        minutes_given: parseInt(this.form.minutes) || 0,
        resolution: this.form.resolution
      }
      if (this.form.resolution === 'recover') {
        incident.recovery = {date: this.form.date, start: this.form.start, duration: parseInt(this.form.recoveryMinutes) || undefined}
      }
      if (this.form.resolution === 'cancel') {
        incident.refund = this.form.refund
      }
      this.saving = true
      this.$http.post(
        this.app.url.api + this.app.endpoints.practice.incident.create,
        {id: this.practiceId, incident: JSON.stringify(incident)},
        {emulateJSON: true, headers: this.headers()}
      ).then(response => {
        this.saving = false
        if (response.status === 200 && response.body.success === true) {
          this.form.open = false
          this.$swal({title: this.s.done, icon: 'success', timer: 1800, showConfirmButton: false})
          this.load()
          this.$emit('done', response.body.data)
          return
        }
        if (response.body && response.body.error && response.body.error.code === 'S100') {
          this.app.methods.logout()
          return
        }
        this.showRequestError(this.app.strings, 'practice-incident', response.body.error)
      }, () => {
        this.saving = false
        this.showError(this.app.strings, 'request', 'status-network: Connection unreachable')
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/variables-app.scss";
@import "@/assets/styles/variables-valkiria.scss";

.incident-sheet {
  margin: 12px 0;
}

.incident-open {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 46px;
  border: 1px solid #f3d3a6;
  border-radius: 14px;
  background-color: #fff8ee;
  color: #b45309;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.incident-done {
  display: flex;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  background-color: #fff8ee;
  color: #92400e;
  font-size: 14px;
}

.incident-done > svg {
  margin-top: 2px;
}

.incident-done > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.incident-backdrop {
  position: fixed;
  inset: 0;
  // Over the top bar, the side menu and the bottom button.
  z-index: 1200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background-color: rgba(15, 23, 42, 0.45);
}

.incident-form {
  width: 100%;
  max-width: 520px;
  max-height: 92vh;
  overflow-y: auto;
  padding: 20px 18px 0;
  border-radius: 22px 22px 0 0;
  background-color: #FFFFFF;
  box-sizing: border-box;
  color: $text-color-1;
}

.incident-form h3 {
  margin: 0 0 10px;
  font-size: 18px;
}

.incident-form label {
  display: block;
  margin: 12px 0 6px;
  color: $text-color-2;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.incident-form textarea,
.incident-form input[type=number],
.incident-form input[type=date],
.incident-form input[type=time] {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid $color-grey-light;
  border-radius: 12px;
  font: inherit;
  box-sizing: border-box;
}

.incident-form textarea {
  min-height: 70px;
}

.incident-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.incident-chips button,
.incident-resolutions button {
  border: 1px solid $color-grey-light;
  border-radius: 999px;
  background-color: $color-grey-light-3;
  color: $text-color-1;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.incident-chips button {
  padding: 6px 12px;
}

.incident-resolutions {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.incident-resolutions button {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 10px 14px;
  border-radius: 14px;
  text-align: left;
}

.incident-resolutions span {
  color: $text-color-2;
  font-size: 12px;
}

.incident-chips button.selected,
.incident-resolutions button.selected {
  border-color: $color-primary-1;
  background-color: #eaf3fb;
  box-shadow: inset 0 0 0 1px $color-primary-1;
}

.incident-row {
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: 0 8px;
}

.incident-row > div:last-child {
  grid-column: 1 / -1;
}

.incident-form label.incident-check {
  display: flex;
  align-items: center;
  gap: 8px;
  text-transform: none;
  font-size: 14px;
  color: $text-color-1;
  font-weight: 500;
}

// Always in sight, however long the form.
.incident-actions {
  display: flex;
  gap: 10px;
  margin-top: 18px;
  position: sticky;
  bottom: 0;
  padding: 12px 0 calc(16px + env(safe-area-inset-bottom, 0px));
  background-color: #FFFFFF;
}

.incident-actions > button {
  flex: 1;
}

.incident-secondary {
  height: 44px;
  border: 1px solid $color-grey-1;
  border-radius: 999px;
  background-color: #FFFFFF;
  color: $text-color-1;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

@media (min-width: 1024px) {
  .incident-backdrop {
    align-items: center;
  }

  .incident-form {
    border-radius: 22px;
  }
}
</style>
